/*
 * Figma `95:2` — Textarea. State: Default, Focus, Error, Disabled, Over limit.
 *
 * The counter is the reason this is not just a taller input. Several editors in the frozen design
 * bound their copy — a Landing tagline at 80 characters, for one — and the design shows the limit
 * being *approached*, not only breached. So the count renders from the first keystroke, and the
 * over-limit state is announced rather than merely coloured.
 *
 * `maxLength` is intentionally not passed to the element when a limit is given: silently swallowing
 * the 81st character reads as a broken keyboard. The field lets the text exceed, says so, and the
 * caller refuses the save.
 */
import { useId } from 'react';
import type { TextareaHTMLAttributes } from 'react';
import { IconWarning } from './icons';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'maxLength'> {
  label: string;
  error?: string | null;
  hint?: string;
  requiredMark?: boolean;
  /** Shows a live count. The field never truncates; over-limit is a state the caller acts on. */
  limit?: number;
}

export function Textarea({
  label, error, hint, requiredMark, limit, className = '', value, rows = 4, ...rest
}: TextareaProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const countId = `${id}-count`;

  const length = typeof value === 'string' ? value.length : 0;
  const over = limit !== undefined && length > limit;
  const describedBy = [
    error ? errorId : null,
    hint ? hintId : null,
    limit !== undefined ? countId : null,
  ].filter(Boolean).join(' ') || undefined;

  return (
    <div className="jt-field">
      <label className="jt-field-label" htmlFor={id}>
        {label}
        {requiredMark && <span className="jt-field-required" aria-hidden="true">*</span>}
      </label>
      <textarea
        {...rest}
        id={id}
        rows={rows}
        value={value}
        className={`jt-input jt-textarea ${className}`.trim()}
        aria-invalid={error || over ? true : undefined}
        aria-describedby={describedBy}
      />
      <div className="jt-field-foot">
        {hint && <div className="jt-field-hint" id={hintId}>{hint}</div>}
        {limit !== undefined && (
          <div
            className="jt-field-count"
            id={countId}
            data-over={over || undefined}
            aria-live="polite"
          >
            {over ? `เกิน ${length - limit} ตัวอักษร` : `${length}/${limit}`}
          </div>
        )}
      </div>
      {error && (
        <div className="jt-field-error" id={errorId}>
          <IconWarning size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
